import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient from '../../../../pages/keycloak/axiosInstance';

const UploadSection = () => {
  // State for form inputs
  const [documentType, setDocumentType] = useState('');
  const [date, setDate] = useState('');
  const [files, setFiles] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [otherDocumentType, setOtherDocumentType] = useState('');
  const [customDocumentTypes, setCustomDocumentTypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ open: false, message: '', type: 'success' });
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileProgresses, setFileProgresses] = useState({}); // Track progress per file
    const [uploadSuccess, setUploadSuccess] = useState(false);

  // Fetch projectId from localStorage and department from URL
  const location = useLocation();
  const department = location.pathname.split('/').filter(Boolean).pop() || '';
  const projectId = localStorage.getItem('selectedProject');
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Hardcoded document types
  const documentTypes = [
    'Factory Assessment Reports',
    'Supplier NCR (After Assessment) closure reports',
    'QAP',
    'Deviations approvals',
    'Supplier 8Ds',
    'Invoice wise Warranty certificates',
    'Third party lab test reports',
    'Raw material reliability reports',
    'Supplier Calibrations reports',
    'Packaging work instructions',
    'Golden and silver modules certificate',
    'ISO 9001 certificate',
    'ISO 18001 certificate',
    'Thermography reports',
    'Warranty Certificates',
    'Site EL Inspection reports',
    'Site Flash testing reports',
    'Work Instruction',
    'Checklist/ Forms',
    'Any Other Reports',
  ];

  // Redirect if projectId or department is missing
  useEffect(() => {
    if (!projectId) {
      setAlert({ open: true, message: 'Please select a project first.', type: 'error' });
      navigate('/project-selection');
    }
    if (!department) {
      setAlert({ open: true, message: 'Department not specified in URL.', type: 'error' });
      navigate('/department-selection');
    }
  }, [projectId, department, navigate]);

  // Fetch custom document types when "Any Other Reports" is selected
  useEffect(() => {
    if (documentType === 'Any Other Reports' && projectId && department) {
      const fetchCustomDocumentTypes = async () => {
        try {
          const response = await apiClient.get(`get_document_types`, {
            params: { projectId, department },
          });
          setCustomDocumentTypes(response.data.documentTypes || []);
        } catch (error) {
          console.error('Error fetching custom document types:', error);
          setAlert({
            open: true,
            message: 'Failed to fetch custom document types.',
            type: 'error',
          });
        }
      };
      fetchCustomDocumentTypes();
    } else {
      setCustomDocumentTypes([]);
    }
  }, [documentType, projectId, department]);

  const handleDocumentTypeChange = (event) => {
    const selectedType = event.target.value;
    setDocumentType(selectedType);
    if (selectedType !== 'Any Other Reports') {
      setOtherDocumentType('');
      setCustomDocumentTypes([]);
    }
    if (errors.documentType) {
      setErrors({ ...errors, documentType: '' });
    }
  };

  const handleDateChange = (event) => {
    setDate(event.target.value);
    if (errors.date) {
      setErrors({ ...errors, date: '' });
    }
  };

  const handleFileChange = (event) => {
    const uploadedFiles = Array.from(event.target.files);
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/png',
      'image/jpeg',
      'image/bmp',
      'application/zip',
      'application/x-zip-compressed',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];
    const maxSize = 1024 * 1024 * 1024; // 1GB
    const validFiles = [];
    const newErrors = [];
    uploadedFiles.forEach((file) => {
      let errorMsg = '';
      if (!allowedTypes.includes(file.type)) {
        errorMsg = `${file.name}: Invalid file type.`;
      } else if (file.size > maxSize) {
        errorMsg = `${file.name}: File size exceeds the limit of 1GB.`;
      } else if (!file.name) {
        errorMsg = 'Selected file has no name.';
      }
      if (errorMsg) {
        newErrors.push(errorMsg);
      } else {
        validFiles.push(file);
      }
    });
    setFiles(validFiles);
    setUploadProgress(0);
    setFileProgresses({}); // Reset progress tracking
    if (newErrors.length > 0) {
      setErrors({ ...errors, file: newErrors.join(' ') });
    } else {
      setErrors({ ...errors, file: '' });
    }
  };

  const handleClearFiles = () => {
    setFiles([]);
    setErrors({ ...errors, file: '' });
    setUploadProgress(0);
    setFileProgresses({});
    // Reset the file input element
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleRemarksChange = (event) => {
    setRemarks(event.target.value);
  };

  const handleOtherDocumentTypeChange = (event) => {
    setOtherDocumentType(event.target.value);
    if (errors.otherDocumentType) {
      setErrors({ ...errors, otherDocumentType: '' });
    }
  };

  const uploadFileInChunks = async (file, fileIndex, totalFiles) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const fileId = `${file.name}-part${Date.now()}`; // Unique identifier for the file

    // Validate required fields before sending request
    if (!projectId || !department || !documentType || !date || !file.name || !file.type) {
      throw new Error('Missing required fields: ensure project, department, document type, date, and valid file are selected.');
    }

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = new Blob([file.slice(start, end)], { type: file.type });
      const formData = new FormData();
      formData.append('fileChunk', chunk, file.name);
      formData.append('fileName', file.name);
      formData.append('fileId', fileId);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('projectId', projectId);
      formData.append('department', department);
      formData.append('documentType', documentType);
      if (documentType === 'Any Other Reports' && otherDocumentType.trim()) {
        formData.append('customDocumentType', otherDocumentType.trim());
      }
      formData.append('date', date);
      formData.append('remark', remarks || '');

      console.log('Sending FormData for chunk', chunkIndex + 1, 'of', totalChunks, 'for file', file.name);
      for (let [key, value] of formData.entries()) {
        console.log(`${key}: ${value instanceof Blob ? `${value.name} (${value.type})` : value}`);
      }

      try {
        const response = await apiClient.post(`upload_supporting_documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const fileProgress = ((chunkIndex * CHUNK_SIZE + progressEvent.loaded) / file.size) * 100;

            // Update progress for this specific file
            setFileProgresses((prev) => {
              const newProgresses = { ...prev, [file.name]: fileProgress };

              // Calculate total average progress across all files
              const totalProgress = Object.values(newProgresses).reduce((sum, p) => sum + p, 0);
              const avgProgress = totalFiles > 0 ? totalProgress / totalFiles : 0;
              setUploadProgress(Math.min(avgProgress, 100));

              return newProgresses;
            });
          },
        });
        console.log(`Chunk ${chunkIndex + 1}/${totalChunks} uploaded successfully:`, response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message;
        console.error(`Failed to upload chunk ${chunkIndex + 1} of ${file.name}:`, errorMessage);
        throw new Error(`Failed to upload chunk ${chunkIndex + 1} of ${file.name}: ${errorMessage}`);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let newErrors = {};

    // Validate required fields
    if (!projectId) {
      newErrors.projectId = 'Project ID is required. Please select a project.';
    }
    if (!department) {
      newErrors.department = 'Department is required. Please ensure the URL includes a department.';
    }
    if (!documentType) {
      newErrors.documentType = 'Please select a document type.';
    } else if (documentType === 'Any Other Reports' && !otherDocumentType.trim()) {
      newErrors.otherDocumentType = 'Please specify the other document type.';
    }
    if (!date) {
      newErrors.date = 'Please select a date.';
    }
    if (files.length === 0) {
      newErrors.file = 'Please select at least one file.';
    }
    if (remarks.trim() && remarks.length > 500) {
      newErrors.remarks = 'Remarks must not exceed 500 characters.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      if (newErrors.projectId || newErrors.department) {
        setAlert({
          open: true,
          message: newErrors.projectId || newErrors.department,
          type: 'error',
        });
      }
      return;
    }

    setLoading(true);
    setFileProgresses({}); // Reset progress tracking before upload

    try {
      // Upload files sequentially
      for (let i = 0; i < files.length; i++) {
        await uploadFileInChunks(files[i], i, files.length);
      }
      
      setUploadSuccess(true);
      setAlert({
        open: true,
        message: 'Files uploaded successfully!',
        type: 'success',
      });

      // Reset form
      setDocumentType('');
      setDate('');
      setFiles([]);
      setRemarks('');
      setOtherDocumentType('');
      setCustomDocumentTypes([]);
      setErrors({});
      setUploadProgress(0);
      setFileProgresses({});

      // Reset file input
      const fileInput = document.getElementById('file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || 'Failed to upload files.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (alert.open) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, open: false });
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [alert.open]);

  return (
    <div className="flex justify-center items-center w-full h-[75vh] bg-gray-100 overflow-hidden">
      {alert.open && (
        <div className={`fixed top-4 right-4 px-4 py-2 rounded-md shadow-md text-white ${alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {alert.message}
          <button onClick={() => setAlert({ ...alert, open: false })} className="ml-2 text-sm font-bold">
            ×
          </button>
        </div>
      )}
      <div className="p-8 w-[99%] max-w-[1200px] h-[99%] mx-auto bg-white rounded-xl shadow-md overflow-y-auto">
        <h1 className="text-2xl font-bold text-center mb-6">UPLOAD SUPPORTING DOCUMENTS</h1>
        <form onSubmit={handleSubmit}>
          <div className="w-full grid lg:grid-cols-2 md:grid-cols-1 gap-4">
            <div>
              <label htmlFor="document-type" className="block text-sm font-medium text-gray-700">
                Document Type
              </label>
              <select
                id="document-type"
                value={documentType}
                disabled={loading}
                onChange={handleDocumentTypeChange}
                className={`mt-1 block w-full px-3 py-2 border ${errors.documentType ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm h-[45px]`}
              >
                <option value="">Select the file...</option>
                {documentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.documentType && (
                <p className="mt-2 text-sm text-red-600">{errors.documentType}</p>
              )}
            </div>
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700">
                Date of Document
              </label>
              <input
                type="date"
                id="date"
                value={date}
                disabled={loading}
                max={new Date().toISOString().split('T')[0]}
                onChange={handleDateChange}
                className={`mt-1 block w-full px-3 py-2 border ${errors.date ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm h-[45px]`}
              />
              {errors.date && <p className="mt-2 text-sm text-red-600">{errors.date}</p>}
            </div>
          </div>
          <div className="mt-6 grid lg:grid-cols-2 sm:grid-cols-1 gap-4">
            <div>
              <label htmlFor="file-upload-label" className="block text-sm font-medium text-gray-700 mb-1">
                Upload Files
              </label>
              <div className="flex items-center border border-gray-300 rounded-md px-3 py-2 focus-within:ring-2 focus-within:ring-gray-500 focus-within:border-gray-500 h-[45px]">
                <label htmlFor="file-upload" className="cursor-pointer flex-shrink-0">
                  <span className="px-4 py-1 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-md">
                    Choose File
                  </span>
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  disabled={loading}
                  accept="image/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.zip,.txt"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <span className="ml-3 text-sm text-gray-700 truncate">
                  {files.length > 0
                    ? files.map((file, index) => (
                        <React.Fragment key={index}>
                          {file.name}
                          {index < files.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))
                    : 'No files selected'}
                </span>
                {files.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearFiles}
                    disabled={loading}
                    className="ml-2 text-lg font-bold text-gray-600 hover:text-gray-800"
                    title="Clear selected files"
                  >
                    ×
                  </button>
                )}
              </div>
              {errors.file && <p className="mt-2 text-sm text-red-600">{errors.file}</p>}
            </div>
            {documentType === 'Any Other Reports' && (
              <div>
                <label htmlFor="other-document-type" className="block text-sm font-medium text-gray-700">
                  Specify Other Document Type
                </label>
                <input
                  id="other-document-type"
                  type="text"
                  list="custom-document-types"
                  disabled={loading}
                  value={otherDocumentType}
                  onChange={handleOtherDocumentTypeChange}
                  className={`mt-1 block w-full px-3 py-2 border ${errors.otherDocumentType ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm h-[45px]`}
                  placeholder="Type or select a document type..."
                />
                <datalist id="custom-document-types">
                  {customDocumentTypes.map((type, index) => (
                    <option key={index} value={type} />
                  ))}
                </datalist>
                {errors.otherDocumentType && (
                  <p className="mt-2 text-sm text-red-600">{errors.otherDocumentType}</p>
                )}
              </div>
            )}
          </div>
          <div className="mt-4">
            <label htmlFor="remarks" className="block text-sm font-medium text-gray-700">
              Remarks
            </label>
            <textarea
              id="remarks"
              value={remarks}
              disabled={loading}
              onChange={handleRemarksChange}
              rows={3}
              className={`mt-1 block w-full px-3 py-2 border ${errors.remarks ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm`}
            />
            {errors.remarks && <p className="mt-2 text-sm text-red-600">{errors.remarks}</p>}
          </div>

          {/* Single Progress Bar for All Files */}
          {files.length > 0 && uploadProgress > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Uploading...</span>
                <span>{Math.round(uploadProgress)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-gray-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {uploadSuccess && (
            <div className="mt-6 text-center text-green-700 font-medium bg-green-50 p-3 rounded-lg border border-green-200">
              Upload successful!
            </div>
          )}

          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={loading}
              className={`w-28 px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 flex items-center justify-center ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-600 hover:bg-gray-700'}`}
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
              ) : (
                'Upload'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadSection;